import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Select,
  TreeSelect,
  Card,
  Spin,
  message,
  Space,
  Row,
  Col,
  InputNumber,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import hqDisplayGroupService from "../../services/hqDisplayGroupService";
import categoryService from "../../services/categoryService";
import {
  VALIDATION_RULES,
  getTranslatedValidationRules,
} from "../../utils/validationRules";
import "../../styles/hqDisplayGroup.css";
import {
  HQDisplayGroup,
  DisplayGroupCategory,
} from "../../types/hqDisplayGroup";
import { Category } from "../../types/category";
import { Item } from "../../types/item";
import ItemListManager from "./components/ItemListManager";
import { Rule } from "antd/es/form";

interface DisplayGroupFormData {
  code: string;
  name: string;
  status: "ENABLE" | "DISABLE" | "Pending" | "DRAFT";
  mainDivision?: string;
  categoryIds: number[];
  subClassSets: DisplayGroupCategory[];
  remark?: string;
  saleDays?: number;
}

const { Option } = Select;
const { TextArea } = Input;

const HQDisplayGroupCreate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  // 获取带有翻译的验证规则
  const translatedValidationRules = getTranslatedValidationRules(t);
  const [loading, setLoading] = useState<boolean>(false);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryTreeData, setCategoryTreeData] = useState<any[]>([]);
  const [showMainDivision, setShowMainDivision] = useState<boolean>(false);
  const [availableDivisions, setAvailableDivisions] = useState<any[]>([]);
  const [whiteListItems, setWhiteListItems] = useState<Item[]>([]);
  const [blackListItems, setBlackListItems] = useState<Item[]>([]);
  const [itemListManagerVisible, setItemListManagerVisible] =
    useState<boolean>(false);
  const [currentListType, setCurrentListType] = useState<"white" | "black">(
    "white"
  );

  // 获取类目数据
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await categoryService.listCategories();
      if (response.success && response.data) {
        setCategories(response.data);
        // 构建树形结构数据
        const treeData = buildCategoryTree(response.data);

        console.log("***", treeData);
        setCategoryTreeData(treeData);
      } else {
        message.error(response.errorMessage || t("hqDisplayGroup.loadFailed"));
      }
    } catch (error) {
      console.error("Failed to fetch category data:", error);
      message.error(t("hqDisplayGroup.loadFailed"));
    } finally {
      setCategoriesLoading(false);
    }
  };

  // 构建类目树形结构
  const buildCategoryTree = (categories: Category[]) => {
    // 按division分组
    const divisionMap = new Map<string, Category[]>();
    categories.forEach((category) => {
      if (!divisionMap.has(category.divisionCode)) {
        divisionMap.set(category.divisionCode, []);
      }
      divisionMap.get(category.divisionCode)!.push(category);
    });

    // 构建树形结构
    const treeData: any[] = [];

    divisionMap.forEach((divisionCategories, divisionCode) => {
      const firstDivisionCategory = divisionCategories[0];
      // 创建事业部节点 - 使用显示名称
      const divisionNode = {
        title: firstDivisionCategory.divisionName, // 使用divisionName而不是divisionCode
        value: `division-${divisionCode}-${Math.random()}`,
        key: `division-${divisionCode}-${Math.random()}`,
        selectable: true, // 允许选择非叶子节点
        children: [] as any[],
      };

      // 按department分组
      const departmentMap = new Map<string, Category[]>();
      divisionCategories.forEach((category) => {
        if (!departmentMap.has(category.departmentCode)) {
          departmentMap.set(category.departmentCode, []);
        }
        departmentMap.get(category.departmentCode)!.push(category);
      });

      departmentMap.forEach((departmentCategories, departmentCode) => {
        const firstDepartmentCategory = departmentCategories[0];
        // 创建部门节点 - 使用显示名称
        const departmentNode = {
          title: firstDepartmentCategory.departmentName, // 使用departmentName而不是departmentCode
          value: `department-${departmentCode}-${Math.random()}`,
          key: `department-${departmentCode}-${Math.random()}`,
          selectable: true, // 允许选择非叶子节点
          children: [] as any[],
        };

        // 按group分组
        const groupMap = new Map<string, Category[]>();
        departmentCategories.forEach((category) => {
          if (!groupMap.has(category.groupCode)) {
            groupMap.set(category.groupCode, []);
          }
          groupMap.get(category.groupCode)!.push(category);
        });

        groupMap.forEach((groupCategories, groupCode) => {
          const firstGroupCategory = groupCategories[0];
          // 创建组节点 - 使用显示名称
          const groupNode = {
            title: firstGroupCategory.groupName, // 使用groupName而不是groupCode
            value: `group-${groupCode}-${Math.random()}`,
            key: `group-${groupCode}-${Math.random()}`,
            selectable: true, // 允许选择非叶子节点
            children: [] as any[],
          };

          // 按class分组
          const classMap = new Map<string, Category[]>();
          groupCategories.forEach((category) => {
            if (!classMap.has(category.classCode)) {
              classMap.set(category.classCode, []);
            }
            classMap.get(category.classCode)!.push(category);
          });

          classMap.forEach((classCategories, classCode) => {
            const firstClassCategory = classCategories[0];
            // 创建class节点 - 使用显示名称
            const classNode = {
              title: firstClassCategory.className, // 使用className而不是classCode
              value: `class-${classCode}-${Math.random()}`,
              key: `class-${classCode}-${Math.random()}`,
              selectable: true, // 允许选择非叶子节点
              children: [] as any[],
            };

            // 添加subclass节点（可选择）
            classCategories.forEach((category) => {
              classNode.children.push({
                title: category.subClassName, // 子类别名称
                value: category.id,
                key: category.id,
                isLeaf: true,
                selectable: true, // 明确设置叶子节点可选
              });
            });

            groupNode.children.push(classNode);
          });

          departmentNode.children.push(groupNode);
        });

        divisionNode.children.push(departmentNode);
      });

      treeData.push(divisionNode);
    });

    return treeData;
  };

  // 组件加载时获取类目数据并设置初始值
  useEffect(() => {
    fetchCategories();

    // 设置表单初始值
    form.setFieldsValue({
      status: "Pending", // 默认状态为待审核
    });
  }, []);

  const handleSubmit = async (values: DisplayGroupFormData) => {
    try {
      setLoading(true);
      // 将表单数据转换为HQDisplayGroup格式
      const displayGroup: Partial<HQDisplayGroup> = {
        code: values.code,
        name: values.name,
        status: values.status,
        mainDivision: values.mainDivision || "",
        subClassSets: values.categoryIds
          .map((id) => {
            const category = categories.find((cat) => cat.id === id);
            return category
              ? {
                  categoryId: category.id,
                  divisionCode: category.divisionCode,
                  divisionName: category.divisionName,
                  departmentCode: category.departmentCode,
                  departmentName: category.departmentName,
                  groupCode: category.groupCode,
                  groupName: category.groupName,
                  classCode: category.classCode,
                  className: category.className,
                  subClassCode: category.subClassCode,
                  subClassName: category.subClassName,
                  creatorAccount: category.creatorAccount,
                  creatorName: category.creatorName,
                  gmtCreate: category.gmtCreate,
                  modifierAccount: category.modifierAccount,
                  modifierName: category.modifierName,
                  gmtModified: category.gmtModified,
                  deleted: category.deleted,
                }
              : null;
          })
          .filter((item) => item !== null),
        whiteListItems: whiteListItems
          ? whiteListItems.map((item) => item.code)
          : [],
        blackListItems: blackListItems
          ? blackListItems.map((item) => item.code)
          : [],
        remark: values.remark,
        saleDays: values.saleDays || 0,
      };

      const response = await hqDisplayGroupService.createHQDisplayGroup(
        displayGroup as HQDisplayGroup
      );

      if (response.success) {
        message.success(t("hqDisplayGroup.createSuccess"));
        navigate("/hq-display-groups");
      } else {
        message.error(
          response.errorMessage || t("hqDisplayGroup.createFailed")
        );
      }
    } catch (error) {
      console.error("Create failed:", error);
      message.error(error.message || t("hqDisplayGroup.createFailed"));
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIds = (categoryIds: number[]) => {
    return (
      categoryIds
        ?.map((id) => {
          const category = categories.find((cat) => cat.id === id);
          return category ? category.subClassCode : null;
        })
        .filter((item) => item !== null) || []
    );
  };

  const handleBack = () => {
    navigate("/hq-display-groups");
  };

  return (
    <div className="hq-display-group-form">
      <div className="form-header">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          {t("hqDisplayGroup.backToList")}
        </Button>
        <h2>{t("hqDisplayGroup.create")}</h2>
      </div>

      <Card className="form-card">
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              status: "DRAFT",
              saleDays: 14,
            }}
          >
            <div className="form-row">
              {/* <Form.Item
                name="code"
                label={t("hqDisplayGroup.code")}
                rules={translatedValidationRules.code as Rule[]}
                className="form-col"
              >
                <Input
                  placeholder={t("hqDisplayGroup.form.codePlaceholder")}
                  maxLength={20}
                />
              </Form.Item> */}

              <Form.Item
                name="name"
                label={t("hqDisplayGroup.name")}
                rules={translatedValidationRules.name as Rule[]}
                className="form-col"
              >
                <Input
                  placeholder={t("hqDisplayGroup.form.namePlaceholder")}
                  maxLength={50}
                />
              </Form.Item>
            </div>

            <div className="form-row">
              <Form.Item
                name="status"
                label={t("common.status")}
                className="form-col"
              >
                <Select disabled>
                  <Option value="DRAFT">
                    {t("hqDisplayGroup.status.draft")}
                  </Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              name="categoryIds"
              label={t("hqDisplayGroup.associatedCategories")}
              rules={translatedValidationRules.categories as Rule[]}
              extra={t("hqDisplayGroup.form.categoriesHelp")}
            >
              <TreeSelect
                treeData={categoryTreeData}
                treeCheckable={true}
                // showCheckedStrategy={TreeSelect.SHOW_CHILD}
                // placeholder={t("hqDisplayGroup.form.selectCategories")}
                // style={{ width: "100%" }}
                // dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                // loading={categoriesLoading}

                // 支持label/value过滤
                // 支持大小写混淆
                filterTreeNode={(inputValue, treeNode) => {
                  const { title, value } = treeNode;
                  return (
                    title
                      ?.toString()
                      ?.toLowerCase()
                      ?.includes(inputValue?.toLowerCase()) ||
                    value
                      ?.toString()
                      ?.toLowerCase()
                      ?.includes(inputValue?.toLowerCase())
                  );
                }}
                treeDefaultExpandAll
                allowClear
                // showSearch
                // treeNodeFilterProp="title"
                maxTagCount={0}
                maxTagPlaceholder={() => {
                  const selectedCount =
                    form.getFieldValue("categoryIds")?.length || 0;
                  return selectedCount > 0
                    ? t("hqDisplayGroup.selectedCount", {
                        count: selectedCount,
                      })
                    : "";
                }}
                // onSelect={(selectedKeys, info) => {
                //   // 处理选中节点，但不做任何操作，由onChange处理
                // }}
                // onDeselect={(selectedKeys, info) => {
                //   // 处理取消选中节点，但不做任何操作，由onChange处理
                // }}
                onChange={(value) => {
                  // 根据节点是否有children来判断是否可选
                  if (Array.isArray(value)) {
                    // 保留所有可选节点的值
                    // 这里不再通过前缀过滤，而是在buildCategoryTree中通过设置selectable属性控制
                    form.setFieldsValue({ categoryIds: value });

                    // 获取所有选中的类目
                    const selectedCategories = categories.filter((cat) =>
                      value.includes(cat.id)
                    );

                    // 检查是否所有类目都属于同一个Division
                    if (selectedCategories.length > 0) {
                      // 获取所有选中类目关联的事业部
                      // 创建一个Map来存储唯一的division信息
                      const divisionMap = new Map();
                      selectedCategories.forEach((cat) => {
                        if (cat.divisionCode) {
                          divisionMap.set(cat.divisionCode, {
                            divisionCode: cat.divisionCode,
                            divisionName: cat.divisionName || cat.divisionCode,
                          });
                        }
                      });

                      // 转换为数组
                      const divisionsArray = Array.from(divisionMap.values());

                      // 更新可选的主要事业部列表
                      setAvailableDivisions(divisionsArray);

                      // 如果只有一个Division，自动设置为mainDivision并隐藏主要事业部字段
                      if (divisionsArray.length === 1) {
                        const division = divisionsArray[0];
                        form.setFieldsValue({
                          mainDivision: division.divisionCode,
                        });
                        setShowMainDivision(false);
                      } else {
                        // 如果跨多个Division，显示主要事业部字段并设为必填
                        setShowMainDivision(true);

                        // 检查当前选择的主要事业部是否在可选列表中
                        const currentMainDivision =
                          form.getFieldValue("mainDivision");
                        if (
                          currentMainDivision &&
                          !divisionsArray.some(
                            (div) => div.divisionCode === currentMainDivision
                          )
                        ) {
                          // 如果不在可选列表中，清空选择
                          form.setFieldsValue({ mainDivision: undefined });
                        }
                      }
                    } else {
                      // 如果没有选择类目，隐藏主要事业部字段
                      setShowMainDivision(false);
                      setAvailableDivisions([]);
                      form.setFieldsValue({ mainDivision: undefined });
                    }
                  }
                }}
              />
            </Form.Item>

            {showMainDivision && (
              <Form.Item
                name="mainDivision"
                label={t("hqDisplayGroup.mainDivision")}
                rules={[
                  {
                    required: true,
                    message: t("hqDisplayGroup.form.mainDivisionRequired"),
                  },
                ]}
                className="form-col"
              >
                <Select
                  placeholder={t("hqDisplayGroup.form.selectMainDivision")}
                  allowClear
                >
                  {availableDivisions.map((division) => (
                    <Option
                      key={division.divisionCode}
                      value={division.divisionCode}
                    >
                      {division.divisionName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              name="saleDays"
              label={t("hqDisplayGroup.saleDays")}
              className="form-col"
              rules={[
                {
                  required: true,
                  message: t("hqDisplayGroup.form.saleDaysPlaceholder"),
                },
              ]}
              initialValue={14}
            >
              <InputNumber
                placeholder={t("hqDisplayGroup.form.saleDaysPlaceholder")}
                min={1}
                max={15}
                defaultValue={14}
                disabled={true}
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label={t("hqDisplayGroup.itemManagement.title")}>
              <Row gutter={16}>
                <Col span={12}>
                  <Button
                    onClick={() => {
                      setCurrentListType("white");
                      setItemListManagerVisible(true);
                    }}
                    icon={<ShoppingOutlined />}
                    style={{ width: "100%" }}
                  >
                    {t("hqDisplayGroup.itemManagement.manageWhitelist", {
                      count: whiteListItems.length,
                    })}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    onClick={() => {
                      setCurrentListType("black");
                      setItemListManagerVisible(true);
                    }}
                    icon={<ShoppingOutlined />}
                    style={{ width: "100%" }}
                  >
                    {t("hqDisplayGroup.itemManagement.manageBlacklist", {
                      count: blackListItems.length,
                    })}
                  </Button>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item
              name="remark"
              label={t("hqDisplayGroup.remark")}
              rules={translatedValidationRules.remark as Rule[]}
            >
              <TextArea
                rows={4}
                placeholder={t("hqDisplayGroup.form.remarkPlaceholder")}
                maxLength={500}
              />
            </Form.Item>

            <Form.Item className="form-actions">
              <Space>
                <Button onClick={handleBack}>
                  {t("hqDisplayGroup.cancel")}
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                >
                  {t("hqDisplayGroup.save")}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>

      {/* 黑白名单管理弹窗 */}
      <ItemListManager
        visible={itemListManagerVisible}
        onClose={() => setItemListManagerVisible(false)}
        whiteListItems={whiteListItems}
        blackListItems={blackListItems}
        onWhiteListChange={setWhiteListItems}
        onBlackListChange={setBlackListItems}
        mainDivision={form.getFieldValue("mainDivision")}
        categoryIds={getCategoryIds(form.getFieldValue("categoryIds"))}
        currentListType={currentListType}
      />
    </div>
  );
};

export default HQDisplayGroupCreate;
